import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {EventsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();

	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_points1(MAT: MaterialsNetworkSopNode) {
				var points1 = MAT.createNode('points');
				points1.setName('points1');
				points1.uiData.setPosition(-200, -100);
				points1.p.color.set([0.10196078431372549, 0.6352941176470588, 0.06274509803921569]);
				points1.params.postCreateSpareParams();
				points1.params.runOnSceneLoadHooks();
				return points1;
			}
			function create_points2(MAT: MaterialsNetworkSopNode) {
				var points2 = MAT.createNode('points');
				points2.setName('points2');
				points2.uiData.setPosition(-200, 50);
				points2.p.size.set(0.1);
				points2.params.postCreateSpareParams();
				points2.params.runOnSceneLoadHooks();
				return points2;
			}
			function create_points3(MAT: MaterialsNetworkSopNode) {
				var points3 = MAT.createNode('points');
				points3.setName('points3');
				points3.uiData.setPosition(-200, 200);
				points3.p.size.set(0.1);
				points3.p.color.set([0.5686274509803921, 0.10588235294117647, 0.7333333333333333]);
				points3.p.useVertexColors.set(true);
				points3.params.postCreateSpareParams();
				points3.params.runOnSceneLoadHooks();
				return points3;
			}
			create_points1(MAT);
			create_points2(MAT);
			create_points3(MAT);
			MAT.uiData.setPosition(-700, 700);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_add1(geo1: GeoObjNode) {
			var add1 = geo1.createNode('add');
			add1.setName('add1');
			add1.uiData.setPosition(-500, 200);
			add1.params.postCreateSpareParams();
			add1.params.runOnSceneLoadHooks();
			return add1;
		}
		function create_color1(geo1: GeoObjNode) {
			var color1 = geo1.createNode('color');
			color1.setName('color1');
			color1.uiData.setComment(
				'with the color node, we copy the value of the position into the color.\nThen, in the material, we set useVertexColors to true, and the color attribute is then displayed.'
			);
			color1.uiData.setPosition(200, 300);
			color1.p.fromAttribute.set(true);
			color1.p.attribName.set('position');
			color1.params.postCreateSpareParams();
			color1.params.runOnSceneLoadHooks();
			return color1;
		}
		function create_fileGLTF1(geo1: GeoObjNode) {
			var fileGLTF1 = geo1.createNode('fileGLTF');
			fileGLTF1.setName('fileGLTF1');
			fileGLTF1.uiData.setPosition(200, -50);
			fileGLTF1.params.postCreateSpareParams();
			fileGLTF1.params.runOnSceneLoadHooks();
			return fileGLTF1;
		}
		function create_hierarchy1(geo1: GeoObjNode) {
			var hierarchy1 = geo1.createNode('hierarchy');
			hierarchy1.setName('hierarchy1');
			hierarchy1.uiData.setPosition(200, 50);
			hierarchy1.p.mode.set(1);
			hierarchy1.params.postCreateSpareParams();
			hierarchy1.params.runOnSceneLoadHooks();
			return hierarchy1;
		}
		function create_icosahedron1(geo1: GeoObjNode) {
			var icosahedron1 = geo1.createNode('icosahedron');
			icosahedron1.setName('icosahedron1');
			icosahedron1.uiData.setPosition(-150, -50);
			icosahedron1.p.detail.set(3);
			icosahedron1.p.pointsOnly.set(true);
			icosahedron1.params.postCreateSpareParams();
			icosahedron1.params.runOnSceneLoadHooks();
			return icosahedron1;
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(-500, 500);
			material1.p.material.set('../MAT/points1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		function create_material2(geo1: GeoObjNode) {
			var material2 = geo1.createNode('material');
			material2.setName('material2');
			material2.uiData.setPosition(-150, 450);
			material2.p.material.set('../MAT/points2');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return material2;
		}
		function create_material3(geo1: GeoObjNode) {
			var material3 = geo1.createNode('material');
			material3.setName('material3');
			material3.uiData.setPosition(200, 650);
			material3.p.material.set('../MAT/points3');
			material3.params.postCreateSpareParams();
			material3.params.runOnSceneLoadHooks();
			return material3;
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(-150, 800);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_scatter1(geo1: GeoObjNode) {
			var scatter1 = geo1.createNode('scatter');
			scatter1.setName('scatter1');
			scatter1.uiData.setPosition(200, 200);
			scatter1.p.pointsCount.set(1000);
			scatter1.params.postCreateSpareParams();
			scatter1.params.runOnSceneLoadHooks();
			return scatter1;
		}
		function create_transform1(geo1: GeoObjNode) {
			var transform1 = geo1.createNode('transform');
			transform1.setName('transform1');
			transform1.uiData.setPosition(-150, 200);
			transform1.p.t.set([0, 0.8851248236299654, 0.8895212294680235]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return transform1;
		}
		function create_transform2(geo1: GeoObjNode) {
			var transform2 = geo1.createNode('transform');
			transform2.setName('transform2');
			transform2.uiData.setPosition(-500, 350);
			transform2.p.t.set([-0.2290163671973744, 0.5260001126615601, 2.6568871476468]);
			transform2.params.postCreateSpareParams();
			transform2.params.runOnSceneLoadHooks();
			return transform2;
		}
		function create_transform3(geo1: GeoObjNode) {
			var transform3 = geo1.createNode('transform');
			transform3.setName('transform3');
			transform3.uiData.setPosition(200, 550);
			transform3.p.t.set([0, 0, -1.0307274029156148]);
			transform3.params.postCreateSpareParams();
			transform3.params.runOnSceneLoadHooks();
			return transform3;
		}
		create_MAT(geo1);
		var add1 = create_add1(geo1);
		var color1 = create_color1(geo1);
		var fileGLTF1 = create_fileGLTF1(geo1);
		var hierarchy1 = create_hierarchy1(geo1);
		var icosahedron1 = create_icosahedron1(geo1);
		var material1 = create_material1(geo1);
		var material2 = create_material2(geo1);
		var material3 = create_material3(geo1);
		var merge1 = create_merge1(geo1);
		var scatter1 = create_scatter1(geo1);
		var transform1 = create_transform1(geo1);
		var transform2 = create_transform2(geo1);
		var transform3 = create_transform3(geo1);
		color1.setInput(0, scatter1);
		hierarchy1.setInput(0, fileGLTF1);
		material1.setInput(0, transform2);
		material2.setInput(0, transform1);
		material3.setInput(0, transform3);
		merge1.setInput(0, material1);
		merge1.setInput(1, material2);
		merge1.setInput(2, material3);
		scatter1.setInput(0, hierarchy1);
		transform1.setInput(0, icosahedron1);
		transform2.setInput(0, add1);
		transform3.setInput(0, color1);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return geo1;
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		function create_MAT(grid: GeoObjNode) {
			var MAT = grid.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_meshStandard1(MAT: MaterialsNetworkSopNode) {
				var meshStandard1 = MAT.createNode('meshStandard');
				meshStandard1.setName('meshStandard1');
				meshStandard1.uiData.setPosition(0, 0);
				meshStandard1.params.postCreateSpareParams();
				meshStandard1.params.runOnSceneLoadHooks();
				return meshStandard1;
			}
			create_meshStandard1(MAT);
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_box1(grid: GeoObjNode) {
			var box1 = grid.createNode('box');
			box1.setName('box1');
			box1.uiData.setPosition(0, 150);
			box1.p.sizes.set([14.100000000000001, 1, 15.000000000000002]);
			box1.p.center.set([0, -0.5, 0]);
			box1.params.postCreateSpareParams();
			box1.params.runOnSceneLoadHooks();
			return box1;
		}
		function create_material1(grid: GeoObjNode) {
			var material1 = grid.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandard1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		create_MAT(grid);
		var box1 = create_box1(grid);
		var material1 = create_material1(grid);
		material1.setInput(0, box1);
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return grid;
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([-0.4450607455842677, 0.12056184442484857, 0.39944034088649893]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return cameraOrbitControls1;
			}
			create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return events1;
		}
		create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([2.72, 1.897431802728435, -2.001289293650634]);
		perspectiveCamera1.p.r.set([-143.49346974556994, 46.653080010520405, 151.70926169569614]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return perspectiveCamera1;
	}
	function create_spotLight1(parentNode: RootManagerNode) {
		var spotLight1 = parentNode.createNode('spotLight');
		spotLight1.setName('spotLight1');
		spotLight1.uiData.setPosition(200, 200);
		spotLight1.flags.display.set(true);
		spotLight1.p.penumbra.set(0.46);
		spotLight1.p.castShadow.set(true);
		spotLight1.p.shadowRes.set([2048, 2048]);
		spotLight1.params.postCreateSpareParams();
		spotLight1.params.runOnSceneLoadHooks();
		return spotLight1;
	}
	function create_polarTransform1(parentNode: RootManagerNode) {
		var polarTransform1 = parentNode.createNode('polarTransform');
		polarTransform1.setName('polarTransform1');
		polarTransform1.uiData.setPosition(200, 100);
		polarTransform1.flags.display.set(false);
		polarTransform1.p.longitude.set(7.2);
		polarTransform1.p.latitude.set(36);
		polarTransform1.p.depth.set(10);
		polarTransform1.params.postCreateSpareParams();
		polarTransform1.params.runOnSceneLoadHooks();
		return polarTransform1;
	}
	var geo1 = create_geo1(root);
	create_grid(root);
	var perspectiveCamera1 = create_perspectiveCamera1(root);
	var spotLight1 = create_spotLight1(root);
	var polarTransform1 = create_polarTransform1(root);
	spotLight1.setInput(0, polarTransform1);

	return {
		scene,
		cameraPath: perspectiveCamera1.path(),
		focusedNodes: [geo1.nodesByType('material')[0]],
		autoLayout: false,
	};
}
// export const matPoints = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/mat/points`,
// 		createData,
// 		relatedNodes: [PointsMatNode],
// 	}),
// ];
